import React from 'react';
import { MoreVertical, Trash2, SmilePlus, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { EmojiPicker } from './EmojiPicker';
import { cn } from '../../lib/utils';

interface MessageProps {
  id: string;
  user: {
    id: string;
    name: string;
    image?: string | null;
    role: string;
  };
  content: string;
  timestamp: string;
  replyTo?: {
    id: string;
    content: string;
    username: string;
  };
  reactions?: {
    emoji: string;
    count: number;
    hasReacted: boolean;
  }[];
  onDelete?: (id: string) => void;
  onReply?: (id: string) => void;
  onReact?: (emoji: string) => void;
  onRemoveReaction?: (emoji: string) => void;
}

export function Message({ 
  id, 
  user, 
  content, 
  timestamp, 
  replyTo,
  reactions = [],
  onDelete,
  onReply,
  onReact,
  onRemoveReaction
}: MessageProps) {
  const { user: currentUser } = useAuth();
  const [showMenu, setShowMenu] = React.useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const emojiPickerRef = React.useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isOwnMessage = currentUser?.id === user.id;

  return (
    <div className="flex gap-3 group hover:bg-background-secondary -mx-2 p-2 rounded-lg relative">
      <img
        src={user.image || `https://api.dicebear.com/7.x/initials/svg?seed=${user.name}`}
        alt={user.name}
        className="w-10 h-10 rounded-full"
      />
      <div className="flex-1">
        {/* Reply reference */}
        {replyTo && (
          <div className="mb-1 text-sm text-text-tertiary flex items-center gap-2">
            <MessageSquare size={12} />
            <span>Antwort auf <span className="text-accent-primary">{replyTo.username}</span></span>
            <span className="text-text-tertiary">•</span>
            <span className="text-text-tertiary line-clamp-1">{replyTo.content}</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <span className="font-medium">{user.name}</span>
          <span className="px-2 py-0.5 bg-background-tertiary rounded-full text-xs text-text-secondary">
            {user.role}
          </span>
          <span className="text-text-tertiary text-xs">{timestamp}</span>
        </div>
        <p className="text-text-secondary mt-1">{content}</p>

        {/* Reactions */}
        {reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {reactions.map(({ emoji, count, hasReacted }) => (
              <button
                key={emoji}
                onClick={() => hasReacted ? onRemoveReaction?.(emoji) : onReact?.(emoji)}
                className={cn(
                  "px-2 py-1 rounded-full text-sm flex items-center gap-1 transition-colors",
                  hasReacted 
                    ? "bg-accent-primary/20 text-accent-primary" 
                    : "bg-background-tertiary hover:bg-background-tertiary/80 text-text-secondary"
                )}
              >
                <span>{emoji}</span>
                <span>{count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => setShowEmojiPicker(true)}
          className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-background-tertiary rounded-full"
        >
          <SmilePlus size={16} />
        </button>
        <button
          onClick={() => onReply?.(id)}
          className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-background-tertiary rounded-full"
        >
          <MessageSquare size={16} />
        </button>
        {isOwnMessage && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-background-tertiary rounded-full"
            >
              <MoreVertical size={16} />
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-1 w-32 bg-background-secondary border border-divider rounded-md shadow-lg z-10">
                <button
                  onClick={() => {
                    onDelete?.(id);
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-red-500 hover:bg-background-tertiary flex items-center gap-2"
                >
                  <Trash2 size={14} />
                  Löschen
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div 
          ref={emojiPickerRef}
          className="absolute right-0 top-full mt-1 z-20"
        >
          <EmojiPicker onSelect={(emoji) => {
            onReact?.(emoji);
            setShowEmojiPicker(false);
          }} />
        </div>
      )}
    </div>
  );
}